import { useState, useRef, useCallback } from "react";

// ── Model ────────────────────────────────────────────────────────────
const MODEL = "claude-haiku-4-5-20251001";

// ── Prompt builder ───────────────────────────────────────────────────
const buildPrompt = ({ talentName, talentTitle, jobTitle, tone }) => {
  const toneDesc = {
    professional: "professional and formal",
    friendly:     "warm and friendly",
    urgent:       "urgent and time-sensitive",
  }[tone] ?? "professional and formal";

  return [
    `Write a short bid invitation message to ${talentName}, a ${talentTitle}`,
    jobTitle ? ` for the role of "${jobTitle}"` : "",
    `. Tone: ${toneDesc}.`,
    " Keep it under 200 characters.",
    " Write only the message body — no subject line, no quotes, no extra commentary.",
  ].join("");
};

// ── Hook ─────────────────────────────────────────────────────────────
const useAIWriter = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error,        setError]        = useState(null);

  // Use refs so callbacks never go stale inside the async loop
  const onChunkRef = useRef(null);
  const onDoneRef  = useRef(null);

  const generate = useCallback(async ({ talentName, talentTitle, jobTitle, tone, onChunk, onDone }) => {
    onChunkRef.current = onChunk;
    onDoneRef.current  = onDone;

    const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
    if (!apiKey) {
      setError("Set REACT_APP_ANTHROPIC_API_KEY in your .env file.");
      return;
    }

    setIsGenerating(true);
    setError(null);
    onChunkRef.current?.("");       // clear textarea before streaming starts

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type":    "application/json",
          "x-api-key":       apiKey,
          "anthropic-version":                      "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model:      MODEL,
          max_tokens: 300,
          stream:     true,
          messages: [{ role: "user", content: buildPrompt({ talentName, talentTitle, jobTitle, tone }) }],
        }),
      });

      if (!res.ok) throw new Error(`API error ${res.status}`);

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText  = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const lines = decoder.decode(value).split("\n").filter((l) => l.startsWith("data: "));

        for (const line of lines) {
          const raw = line.slice(6).trim();
          if (!raw || raw === "[DONE]") continue;
          try {
            const evt = JSON.parse(raw);
            if (evt.type === "content_block_delta" && evt.delta?.text) {
              fullText += evt.delta.text;
              onChunkRef.current?.(fullText);
            }
          } catch { /* ignore malformed SSE frames */ }
        }
      }

      onDoneRef.current?.(fullText);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return { generate, isGenerating, error };
};

export default useAIWriter;
